/**
 * WageCsvImport — import CSV des taux de salaire CCQ (aperçu puis import en nouvelles versions datées). Batch #14.298.
 */
import { useRef, useState } from "react";
import axios from "axios";
import { Upload, Download, Check, X } from "lucide-react";
import { toast } from "@/lib/toast";
import { API, authH, Btn, thCls, tdCls, err } from "../_shared";

const EXAMPLE = "secteur;annexe;region;metier;qualification;periode;taux;debut;fin;source\nic;standard;;Charpentier-menuisier;compagnon;;46.12;2026-04-26;;Convention IC 2025-2029\nic;standard;;Charpentier-menuisier;apprenti;1;25.37;2026-04-26;;Convention IC 2025-2029\n";

export default function WageCsvImport({ masterId, meta, onImported }) {
  const ref = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const post = async (f, dry) => {
    const fd = new FormData(); fd.append("file", f);
    return axios.post(`${API}/admin/payroll/${masterId}/ccq/wage-rates/import?dry_run=${dry}`, fd, { headers: authH() });
  };
  const pick = async (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f) return;
    setBusy(true);
    try { const r = await post(f, true); setFile(f); setPreview(r.data); } catch (x) { err(x); } finally { setBusy(false); }
  };
  const confirm = async () => {
    setBusy(true);
    try {
      const r = await post(file, false);
      toast.success(`${r.data.added} taux ajouté(s) · ${r.data.closed} ancienne(s) version(s) fermée(s)`);
      onImported(r.data.config); setPreview(null); setFile(null);
    } catch (x) { err(x); } finally { setBusy(false); }
  };
  const template = () => {
    const url = URL.createObjectURL(new Blob(["\ufeff" + EXAMPLE], { type: "text/csv;charset=utf-8" }));
    const a = document.createElement("a"); a.href = url; a.download = "taux_ccq_modele.csv"; a.click(); setTimeout(() => URL.revokeObjectURL(url), 2000);
  };
  const lbl = (list, code) => (list || []).find((o) => o.code === code)?.label || code || "—";
  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50/30 p-3 space-y-2" data-testid="paie-ccq-csv">
      <div className="flex flex-wrap items-center gap-2">
        <input ref={ref} type="file" accept=".csv,text/csv" className="hidden" onChange={pick} data-testid="paie-ccq-csv-input" />
        <Btn tone="slate" icon={Upload} busy={busy && !preview} onClick={() => ref.current?.click()} testId="paie-ccq-csv-pick">Importer des taux (CSV)</Btn>
        <Btn tone="ghost" icon={Download} onClick={template} testId="paie-ccq-csv-template">Modèle CSV</Btn>
        <span className="text-[11px] text-slate-500">Colonnes : {(meta.csv_columns || []).join(" ; ")} — un taux de la même clé déjà en vigueur est fermé la veille de la nouvelle date.</span>
      </div>
      {preview && (
        <div className="space-y-2" data-testid="paie-ccq-csv-preview">
          <p className="text-xs text-slate-700"><b>{preview.count}</b> taux lisible(s) dans « {file?.name} » — aperçu :</p>
          <div className="overflow-x-auto bg-white rounded-lg border border-slate-200">
            <table className="w-full text-xs">
              <thead><tr><th className={thCls}>Secteur · annexe</th><th className={thCls}>Métier</th><th className={thCls}>Qualification</th><th className={`${thCls} text-right`}>Taux</th><th className={thCls}>Du → au</th></tr></thead>
              <tbody className="divide-y divide-slate-100">
                {preview.items.slice(0, 8).map((r, i) => (
                  <tr key={i}><td className={tdCls}>{lbl(meta.sectors, r.sector)} · {lbl(meta.schedules, r.schedule)}{r.region ? ` · ${lbl(meta.regions, r.region)}` : ""}</td><td className={tdCls}>{r.trade_label}</td>
                    <td className={tdCls}>{lbl(meta.qualifications, r.qualification)}{r.period ? ` — période ${r.period}` : ""}</td><td className={`${tdCls} text-right font-mono`}>{Number(r.hourly_rate).toFixed(2)} $</td><td className={tdCls}>{r.effective_from === "1900-01-01" ? "toujours" : r.effective_from} → {r.effective_to || "…"}</td></tr>
                ))}
              </tbody>
            </table>
            {preview.items.length > 8 && <p className="px-3 py-1.5 text-[11px] text-slate-500">… et {preview.items.length - 8} autre(s)</p>}
          </div>
          <div className="flex gap-2 justify-end">
            <Btn tone="slate" icon={X} onClick={() => { setPreview(null); setFile(null); }} testId="paie-ccq-csv-cancel">Annuler</Btn>
            <Btn tone="orange" icon={Check} busy={busy} onClick={confirm} testId="paie-ccq-csv-confirm">Importer {preview.count} taux</Btn>
          </div>
        </div>
      )}
    </div>
  );
}
